import Debug from 'debug'
import { Result, ok, err } from '../../../tracking-api';
import { reportNonFatalError } from '@/app/error-handling';

const debug = Debug('collection-data-store');

interface Details {
  id: string;
}

interface IndexState {
  count: number;
  processedIndex: number;
  indexMap: Map<string, number[]>;
}

export class CollectionDataStore<T extends Details, TError> {

  private state: IndexState | null = null;

  constructor(private queryCount: () => Promise<Result<number, TError>>,
              private queryId: (index: number) => Promise<Result<string, TError>>,
              private load: (index: number) => Promise<Result<T, TError>>,
              private save: (index: number, details: T) => Result<true, TError>
             ) {
  }

  public async upsert(details: T) {

    debug('upsert', details)

    const prefetchResult = await this.prefetchCount();
    if (!prefetchResult.success) {
      return err(prefetchResult.error);
    }

    const state = this.state!;
    debug('current state', state)

    const indexResult = await this.queryLastIndex(details.id, state);
    debug('last index result', indexResult)
    if (!indexResult.success) {
      return err(indexResult.error);
    }

    if (indexResult.value === null) {
      debug('inserting at the end')
      return this.insertAtEnd(details);
    } else {
      let index = indexResult.value;
      if (!index && index !== 0) {
        reportNonFatalError('anomaly: index is undefined')
        index = state.count
      }

      debug('inserting at the index', index)
      return this.saveItem(index, details);
    }
  }

  public async insert(details: T) {
    const prefetchResult = await this.prefetchCount();
    if (!prefetchResult.success) {
      return err(prefetchResult.error);
    }

    this.insertAtEnd(details);

    return ok(true as true);
  }

  public async queryLast(id: string): Promise<Result<T | 'not-found', TError>> {

    const prefetchResult = await this.prefetchCount();
    if (!prefetchResult.success) {
      return err(prefetchResult.error);
    }

    const state = this.state!;
    const lastIndex = await this.queryLastIndex(id, state);
    if (!lastIndex.success) {
      return err(lastIndex.error);
    }

    if (lastIndex.value === null) {
      return ok('not-found');
    }

    const loaded = await this.loadItem(lastIndex.value);
    if (!loaded.success) {
      return err(loaded.error);
    }

    return ok(loaded.value);
  }

  public async queryAll(id: string): Promise<Result<T[], TError>> {
    const prefetchResult = await this.prefetchCount();
    if (!prefetchResult.success) {
      return err(prefetchResult.error);
    }

    const state = this.state!;
    const indices = await this.queryIndices(id, state);
    if (!indices.success) {
      return err(indices.error);
    }

    const result: T[] = [];
    for (const index of indices.value) {
      const loadResult = await this.loadItem(index);
      if (!loadResult.success) {
        return err(loadResult.error);
      }

      result.push(loadResult.value);
    }

    return ok(result);
  }

  private insertAtEnd(details: T) {
    const index = this.state!.count;
    const saveResult = this.saveItem(index, details);
    if (!saveResult.success) {
      return err(saveResult.error);
    }

    this.state!.count++;
    this.recordIdIndex(this.state!, details.id, index);

    return ok(true as true);
  }

  private saveItem(index: number, details: T) {

    return this.save(index, details);
  }

  private loadItem(index: number) {

    return this.load(index);
  }

  private async prefetchCount() {

    if (this.state !== null) {
      return ok('already-fetched');
    }

    const countResult = await this.queryCount();
    if (!countResult.success) {
      return err(countResult.error);
    }

    this.state = {
      count: countResult.value,
      processedIndex: countResult.value,
      indexMap: new Map<string, number[]>()
    };

    return ok('fetched');
  }

  private async queryLastIndex(id: string, state: IndexState): Promise<Result<number | null, TError>> {
    let indices = state.indexMap.get(id);
    if (indices) {
      return ok(indices[0]);
    }

    const fetchResult = await this.prefetchIndices(state, id);
    if (!fetchResult.success) {
      return err(fetchResult.error);
    }

    indices = state.indexMap.get(id);
    if (!indices) {
      return ok(null);
    }

    return ok(indices[0]);
  }

  private async queryIndices(id: string, state: IndexState): Promise<Result<number[], TError>> {
    if (state.processedIndex > 0) {
      const fetchResult = await this.prefetchIndices(state);
      if (!fetchResult.success) {
        return err(fetchResult.error);
      }
    }

    const indices = state.indexMap.get(id);
    if (!indices) {
      return ok([]);
    }

    return ok([ ...indices].sort());
  }

  private async prefetchIndices(state: IndexState, stopAtId?: string) {

    let index = state.processedIndex;
    let found = false;
    index--;
    while (index >= 0 && !found) {
      const idResult = await this.queryId(index);
      if (!idResult.success) {
        return (err(idResult.error));
      }

      this.recordIdIndex(state, idResult.value, index);
      if (stopAtId) {
        found = idResult.value === stopAtId;
      }
      index--;
    }

    return ok(true);
  }

  private recordIdIndex(state: IndexState,
                        id: string,
                        index: number) {

    let indices: number[];
    const found = state.indexMap.get(id);
    if (found) {
      indices = found;
    } else {
      indices = [];
      state.indexMap.set(id, indices);
    }

    indices.push(index);
    if (index < state.processedIndex) {
      state.processedIndex = index;
    }
  }
}
